import { useMemo, useRef } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Button,
  Checkbox,
  cn,
  Input,
  Progress,
} from '@eureca/ui';
import { ConsultantsMenu } from '@/shared/consultants-menu/ConsultantsMenu';
import { SearchIcon } from './icons';
import { JobHeaderLogo } from '@/features/jobs/job-shared/JobHeaderLogo';
import type { CourseArea, CourseGroup } from './courses-seed';

export interface JobEditCoursesPageProps {
  jobName: string;
  companyLogoUrl?: string | null;
  companyName?: string | null;
  /**
   * Areas com os grupos de curso, **ja filtradas pelo backend** (tipo de curso
   * do step anterior + a busca, quando houver). EU-718 - a filtragem saiu do
   * SPA: o Core casa nome de area OU nome de grupo e devolve so o que casa.
   */
  areas: readonly CourseArea[];
  /**
   * Total de areas do catalogo SEM a busca aplicada - usado no rodape
   * ("N areas no total"). Vem da entrada de cache da query sem `query`.
   */
  totalAreasCount: number;
  /**
   * The trimmed search query — only used for the empty-state message
   * ("Nenhuma área encontrada para …"). The `<Input>` value is bound to
   * `searchInputValue` separately.
   */
  searchQuery: string;
  /**
   * Live value of the `<Input>`. Decoupled from `searchQuery` so the input
   * stays instant even while the debounced filter lags 250ms behind.
   */
  searchInputValue: string;
  selectedCourseGroupIds: number[];
  expandedAreaIds: string[];
  progress: number;
  /**
   * `true` enquanto o PATCH de "Salvar alterações" está em voo. Liga o
   * loading spinner no botão de submit.
   */
  isSubmitting: boolean;
  /**
   * Mensagem do cap (cap 500 do Core). Aparece inline acima da lista
   * quando o consultor tenta adicionar o 501º grupo de curso.
   */
  capError?: string;
  maxRequiredCourseGroups: number;
  /**
   * `false` quando a vaga não tem `graduationTypes` (EU-162 FR-088e):
   * a tela renderiza empty state com link de Voltar pra `/formacao`.
   */
  hasGraduationTypes: boolean;
  /**
   * EU-569 — vaga Encerrada / Cancelada: modo somente visualização. Os
   * checkboxes de grupo/área ficam desabilitados; busca e acordeões seguem
   * funcionando (visualizar o que está preenchido). "Avançar" só navega.
   */
  readOnly?: boolean;
  onSearchChange: (value: string) => void;
  onExpandedChange: (areaIds: string[]) => void;
  onToggleCourseGroup: (courseGroupId: number) => void;
  onToggleArea: (areaId: number) => void;
  onBack: () => void;
  onSubmit: () => void;
}

export function JobEditCoursesPage({
  jobName,
  companyLogoUrl,
  companyName,
  areas,
  totalAreasCount,
  searchQuery,
  searchInputValue,
  selectedCourseGroupIds,
  expandedAreaIds,
  progress,
  isSubmitting,
  capError,
  maxRequiredCourseGroups,
  hasGraduationTypes,
  readOnly = false,
  onSearchChange,
  onExpandedChange,
  onToggleCourseGroup,
  onToggleArea,
  onBack,
  onSubmit,
}: JobEditCoursesPageProps) {
  const trimmedQuery = searchQuery.trim();
  const selectedSet = useMemo(() => new Set(selectedCourseGroupIds), [selectedCourseGroupIds]);
  // Lookup for lazy-mount: only render an area's course-group list when its
  // accordion is expanded.
  const expandedSet = useMemo(() => new Set(expandedAreaIds), [expandedAreaIds]);

  const totalSelected = selectedCourseGroupIds.length;
  const counterLabel =
    totalSelected === 0
      ? `${totalAreasCount} áreas`
      : `${totalSelected} grupo${totalSelected === 1 ? '' : 's'} de curso selecionado${
          totalSelected === 1 ? '' : 's'
        } de ${maxRequiredCourseGroups}`;

  // EU-162 FR-088e: empty state quando a vaga não tem `graduationTypes`.
  // O BFF abortaria com 400; a query usa `enabled: false`. Aqui renderizamos
  // mensagem orientando o consultor a voltar para `/formacao` e selecionar
  // tipos primeiro.
  if (!hasGraduationTypes) {
    return (
      <div className="flex min-h-dvh flex-row bg-muted">
        <ConsultantsMenu activeId="jobs" />
        <main className="flex flex-1 flex-col items-center justify-center gap-8 bg-card px-8 py-16">
          <div
            className="flex w-full max-w-[480px] flex-col items-center gap-5 text-center"
            data-testid="job-courses-empty-no-graduation-types"
          >
            <div className="flex flex-col items-center gap-2">
              <h2 className="text-2xl font-semibold leading-none tracking-tight text-foreground">
                Selecione tipos de formação primeiro
              </h2>
              <p className="text-base leading-normal text-muted-foreground">
                Para escolher cursos específicos, é preciso definir antes os tipos de formação
                aceitos pela vaga.
              </p>
            </div>
            <Button type="button" size="lg" onClick={onBack} data-testid="job-courses-back">
              Voltar para Formação
            </Button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="flex h-dvh flex-row overflow-hidden bg-muted">
      <ConsultantsMenu activeId="jobs" />

      <main className="relative flex h-dvh flex-1 flex-col overflow-hidden bg-card">
        <div className="flex-1 overflow-y-auto px-4 pt-8 pb-32 md:px-8">
          <div className="mx-auto flex w-full max-w-[630px] flex-col gap-6">
            <header className="flex flex-col gap-2">
              <div className="flex flex-row items-center gap-2">
                <JobHeaderLogo logoUrl={companyLogoUrl} companyName={companyName} />
                <h1 className="text-3xl font-medium leading-none tracking-tight text-foreground md:text-4xl">
                  {jobName}
                </h1>
              </div>
              <p className="text-xl font-medium leading-none tracking-tight text-accent-foreground md:text-2xl">
                Formação
              </p>
            </header>

            <Progress value={progress} className="w-full bg-secondary" />

            <div className="relative">
              <SearchIcon className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={searchInputValue}
                onChange={(event) => onSearchChange(event.target.value)}
                placeholder="Pesquise por áreas ou cursos"
                className="pl-9"
                data-testid="job-courses-search"
              />
            </div>

            <div className="flex flex-row items-center justify-between text-sm leading-5">
              <span className="text-muted-foreground">{counterLabel}</span>
              {totalSelected > 0 ? (
                <span className="font-medium text-foreground">
                  {totalAreasCount} áreas no total
                </span>
              ) : null}
            </div>

            {capError ? (
              <p
                className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm leading-5 text-destructive"
                role="alert"
                data-testid="job-courses-cap-error"
              >
                {capError}
              </p>
            ) : null}

            {areas.length === 0 ? (
              <p className="py-8 text-center text-sm text-muted-foreground">
                Nenhuma área ou grupo de curso encontrado para “{trimmedQuery}”.
              </p>
            ) : (
              <Accordion
                type="multiple"
                value={expandedAreaIds}
                onValueChange={onExpandedChange}
                className="flex flex-col"
                data-testid="job-courses-list"
              >
                {areas.map((area) => {
                  // EU-718 - `area.courseGroups` JA e o conjunto visivel (o
                  // backend filtrou). Com busca ativa, o checkbox da area e o
                  // contador operam sobre o que esta na tela; sem busca (o
                  // caso normal) e o conjunto completo da area para os tipos
                  // da vaga.
                  const areaGroupIds = area.courseGroups.map((group) => group.id);
                  const selectedInArea = areaGroupIds.filter((id) => selectedSet.has(id));
                  const checkboxState = computeCheckboxState(
                    selectedInArea.length,
                    areaGroupIds.length
                  );
                  // Lazy mount: keep `<AccordionContent>` in the DOM (Radix
                  // expects it for `data-state` transitions) but skip the
                  // list until the area is expanded. With ~20 closed areas at
                  // boot, the initial DOM is just the accordion frame.
                  const isExpanded = expandedSet.has(String(area.id));

                  return (
                    <AccordionItem
                      key={area.id}
                      value={String(area.id)}
                      className="border-b border-border"
                    >
                      <div className="flex flex-row items-center gap-3 py-1">
                        <span
                          className="flex shrink-0 items-center justify-center pl-1"
                          onClick={(event) => event.stopPropagation()}
                        >
                          <Checkbox
                            checked={checkboxState}
                            onCheckedChange={() => onToggleArea(area.id)}
                            disabled={readOnly}
                            className="size-4"
                            aria-label={`Selecionar todos os grupos de curso de ${area.name}`}
                            data-testid={`job-courses-area-${String(area.id)}`}
                          />
                        </span>
                        <AccordionTrigger className="flex-1 py-3 text-base font-medium hover:no-underline">
                          <span className="flex flex-1 flex-row items-center justify-between gap-3 pr-2">
                            <span className="truncate text-foreground">{area.name}</span>
                            <span className="shrink-0 text-xs font-normal text-muted-foreground">
                              {selectedInArea.length}/{areaGroupIds.length}
                            </span>
                          </span>
                        </AccordionTrigger>
                      </div>
                      <AccordionContent>
                        {isExpanded ? (
                          <VirtualizedCourseGroupList
                            courseGroups={area.courseGroups}
                            selectedSet={selectedSet}
                            onToggleCourseGroup={onToggleCourseGroup}
                            readOnly={readOnly}
                          />
                        ) : null}
                      </AccordionContent>
                    </AccordionItem>
                  );
                })}
              </Accordion>
            )}
          </div>
        </div>

        <div className="pointer-events-none absolute inset-x-0 bottom-0 z-10 flex flex-col">
          <div className="h-16 bg-linear-to-b from-white/0 to-white" />
          <div className="bg-card px-4 pb-8 md:px-8">
            <div className="pointer-events-auto mx-auto flex w-full max-w-[630px] flex-row justify-between gap-2">
              <Button
                type="button"
                variant="outline"
                size="lg"
                onClick={onBack}
                data-testid="job-courses-back"
              >
                Voltar
              </Button>
              <Button
                type="button"
                size="lg"
                loading={isSubmitting}
                onClick={onSubmit}
                data-testid="job-courses-submit"
              >
                Avançar
              </Button>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

/**
 * EU-162 FR-086 / EU-718: virtualização dos GRUPOS de curso dentro de cada
 * área expandida. Áreas (até ~40) ficam em DOM normal; grupos por área
 * (até ~90 no pior caso) viram virtualizados via `@tanstack/react-virtual`.
 * Heights homogêneos (40px); overscan 10. DOM mantém apenas itens visíveis +
 * buffer.
 */
interface VirtualizedCourseGroupListProps {
  courseGroups: CourseGroup[];
  selectedSet: Set<number>;
  onToggleCourseGroup: (courseGroupId: number) => void;
  /** EU-569 — desabilita os checkboxes em modo visualização. */
  readOnly?: boolean;
}

const COURSE_GROUP_ROW_HEIGHT = 40;
const COURSE_GROUP_LIST_MAX_HEIGHT = 360;
const COURSE_GROUP_LIST_OVERSCAN = 10;

function VirtualizedCourseGroupList({
  courseGroups,
  selectedSet,
  onToggleCourseGroup,
  readOnly = false,
}: VirtualizedCourseGroupListProps) {
  const parentRef = useRef<HTMLDivElement>(null);
  const virtualizer = useVirtualizer({
    count: courseGroups.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => COURSE_GROUP_ROW_HEIGHT,
    overscan: COURSE_GROUP_LIST_OVERSCAN,
  });

  // Heurística: se a lista é curta (≤8 grupos), renderiza tudo sem
  // virtualização — overhead do scroll container fica desnecessário.
  if (courseGroups.length <= 8) {
    return (
      <ul className="flex flex-col gap-2 pl-7">
        {courseGroups.map((group) => (
          <CourseGroupRow
            key={group.id}
            courseGroup={group}
            isChecked={selectedSet.has(group.id)}
            onToggle={() => onToggleCourseGroup(group.id)}
            readOnly={readOnly}
          />
        ))}
      </ul>
    );
  }

  return (
    <div
      ref={parentRef}
      className="pl-7"
      style={{
        height: `${Math.min(
          courseGroups.length * COURSE_GROUP_ROW_HEIGHT,
          COURSE_GROUP_LIST_MAX_HEIGHT
        )}px`,
        overflow: 'auto',
        contain: 'content',
      }}
      data-testid="job-courses-virtual-list"
    >
      <div
        style={{
          height: `${virtualizer.getTotalSize()}px`,
          width: '100%',
          position: 'relative',
        }}
      >
        {virtualizer.getVirtualItems().map((virtualRow) => {
          const group = courseGroups[virtualRow.index];
          if (!group) return null;
          return (
            <div
              key={group.id}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: `${virtualRow.size}px`,
                transform: `translateY(${virtualRow.start}px)`,
              }}
            >
              <CourseGroupRow
                courseGroup={group}
                isChecked={selectedSet.has(group.id)}
                onToggle={() => onToggleCourseGroup(group.id)}
                readOnly={readOnly}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}

interface CourseGroupRowProps {
  courseGroup: CourseGroup;
  isChecked: boolean;
  onToggle: () => void;
  /** EU-569 — desabilita o checkbox em modo visualização. */
  readOnly?: boolean;
}

function CourseGroupRow({
  courseGroup,
  isChecked,
  onToggle,
  readOnly = false,
}: CourseGroupRowProps) {
  return (
    <label
      className={cn(
        'flex h-10 flex-row items-center gap-3 rounded-md text-sm leading-5 transition-colors',
        readOnly ? 'cursor-not-allowed' : 'cursor-pointer',
        isChecked ? 'text-foreground' : 'text-muted-foreground hover:text-foreground'
      )}
    >
      <Checkbox
        checked={isChecked}
        onCheckedChange={onToggle}
        disabled={readOnly}
        className="size-4"
        data-testid={`job-courses-course-group-${String(courseGroup.id)}`}
      />
      <span className="flex-1">{courseGroup.name}</span>
      <span className="shrink-0 text-xs font-normal text-muted-foreground">
        {courseGroup.courseTypeName}
      </span>
    </label>
  );
}

function computeCheckboxState(selected: number, total: number): boolean | 'indeterminate' {
  if (selected === 0) return false;
  if (selected === total) return true;
  return 'indeterminate';
}
